/**
 * GA4 analytics for the fantasy SPA.
 *
 * The React app (everything under /app) historically fired NOTHING — only the
 * static marketing landing (index.php) carried the tag. This wires the same
 * shared GA4 property the rest of DraftEdge uses (G-MEWCEVEL81) into the app so
 * pageviews + product events land in ONE property, with the cross-domain linker
 * so an in-app "Get the Kit" hop to draftedge.com stays a single session.
 *
 * Reusing the same measurement ID across draftedge.com + fantasy.draftedge.com
 * is fully supported by GA4 (that's what the linker + `accept_incoming` are for)
 * — it is NOT a policy problem the way sharing an AdSense unit would be.
 *
 * Gated to real *.draftedge.com hosts, so localhost dev and the Capacitor
 * native WebView (served from capacitor://) never pollute production analytics.
 * When disabled, every function is a safe no-op.
 */

const GA_MEASUREMENT_ID = 'G-MEWCEVEL81';
const LINKER_DOMAINS = ['draftedge.com', 'fantasy.draftedge.com'];

type GtagArgs =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['set', string, Record<string, unknown>]
  | ['event', string, Record<string, unknown>?];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

let initialized = false;

/** Only track on the live web hosts — never localhost or the native app. */
export function analyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'draftedge.com' || h.endsWith('.draftedge.com');
}

/**
 * Inject gtag.js once and configure the shared property with the cross-domain
 * linker. Page views are sent manually (send_page_view:false) because this is a
 * client-routed SPA — gtag's auto pageview only fires on the initial HTML load.
 */
export function initAnalytics(): void {
  if (initialized || !analyticsEnabled()) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  // Use the exact arguments object shape gtag expects (not a rest array).
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as Window['gtag'];

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.gtag!('js', new Date());
  window.gtag!('set', 'linker', {
    domains: LINKER_DOMAINS,
    accept_incoming: true,
  });
  window.gtag!('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Fire a manual page_view for a client-side route change. */
export function trackPageView(path: string, title?: string): void {
  if (!analyticsEnabled() || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

/** Fire a custom event. No-ops safely when analytics is disabled. */
export function track(name: string, params?: Record<string, unknown>): void {
  if (!analyticsEnabled() || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params ?? {});
}
